import {
  Button,
  FormLabel,
  HStack,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  TrackedButton,
  TrackedButtonProps,
} from '$/components/common/Button/TrackedButton';
import { TrackedSection } from '$/components/common/TrackedSection';
import { useTrackInteraction } from '$/hooks/useTrackInteraction';
import { trackContentImpression } from '$/logger';
import { useFavoriteModal } from '$/pages/DashboardPages/pages/Favorite/hooks/useFavoriteModal';
import { DEFAULTFOLDERNAME, useFavoriteStore } from '$/stores/useFavoriteStore';
import { useMaterialContextMenuStore } from '$/stores/useMaterialContextMenuStore';
import { PiwikContentPiece } from '$/utils/piwikUtils';

type ModalInfo = {
  header: string;
  body?: ReactNode;
  footer?: ReactNode;
};

export const FavoriteModal = () => {
  const { t } = useTranslation();
  const { isOpen, onClose, modalMode, itemIdentifier, itemType, itemName } =
    useFavoriteModal();
  const deleteFolder = useFavoriteStore.useRemoveFolder();
  const removeFavorite = useFavoriteStore.useRemoveFavorite();
  const renameFolder = useFavoriteStore.useRenameFolder();
  const addFavoriteToFolder = useFavoriteStore.useAddFavoriteToFolder();
  const addFolder = useFavoriteStore.useAddFolder();
  const folders = useFavoriteStore.useFavoriteFolders();
  const favorites = useFavoriteStore.useFavorites();
  const setActiveMaterialContextMenuId =
    useMaterialContextMenuStore.useSetActiveMaterial();

  const [nameInputValue, setNameInputValue] = useState('');

  const getCurrentContentPiece = useCallback(():
    | PiwikContentPiece
    | undefined => {
    switch (modalMode) {
      case 'deleteFavorite':
        return 'ManageFavorite';
      case 'createFolder':
        return 'NewFolder';
    }
  }, [modalMode]);

  useEffect(() => {
    if (isOpen) {
      const contentPiece = getCurrentContentPiece();

      if (!contentPiece) {
        return;
      }

      trackContentImpression('Favorite', contentPiece, 'Open');
    }
  }, [isOpen, modalMode, getCurrentContentPiece]);

  const trackCloseButton = useTrackInteraction(
    'Favorite',
    getCurrentContentPiece(),
    'Close',
  );

  const buildFooter = (
    primaryButtonText: string,
    primaryAction: () => void,
    options?: {
      primaryButtonCustomProps?: TrackedButtonProps;
      customSecondaryButton?: ReactNode;
    },
  ) => (
    <>
      {options?.customSecondaryButton ?? (
        <Button
          px='0'
          fontSize='sm'
          onClick={() => {
            setNameInputValue('');
            onClose();
          }}
          variant='text'
        >
          {t('favorites.cancelButton')}
        </Button>
      )}
      <TrackedButton
        fontSize='sm'
        {...options?.primaryButtonCustomProps}
        onClick={() => {
          primaryAction();
          onClose();
        }}
      >
        {primaryButtonText}
      </TrackedButton>
    </>
  );

  useEffect(() => {
    const initialInputValue =
      folders.find((folder) => folder.id === itemIdentifier)?.name ?? '';

    setNameInputValue(initialInputValue);
  }, [folders, itemIdentifier]);

  const { body, header, footer }: ModalInfo = (() => {
    switch (modalMode) {
      case 'deleteFavorite': {
        const favorite = favorites.find(
          (favorite) => favorite.materialId === itemIdentifier,
        );

        return {
          header: t('favorites.removeFavoriteHeader'),
          body: (
            <>
              <Trans
                i18nKey='favorites.removeFavoriteInformation'
                values={{ materialName: itemName }}
              />
              <UnorderedList mt='4' fontSize='sm'>
                {favorite?.parentFolderIds.includes(DEFAULTFOLDERNAME) && (
                  <ListItem>{t('favorites.generalStorage')}</ListItem>
                )}
                {folders
                  .filter((folder) =>
                    favorite?.parentFolderIds.includes(folder.id),
                  )
                  .map((folder) => (
                    <ListItem key={folder.id}>{folder.name}</ListItem>
                  ))}
              </UnorderedList>
            </>
          ),
          footer: buildFooter(
            t('favorites.removeFavoriteHeader'),
            () => removeFavorite(itemIdentifier),
            {
              primaryButtonCustomProps: {
                variant: 'danger',
                contentName: 'Favorite',
                contentPiece: 'ManageFavorite',
                contentTarget: 'Delete',
              },
              customSecondaryButton: (
                <TrackedButton
                  p='0'
                  fontSize='sm'
                  onClick={() => {
                    trackContentImpression('Favorite', 'EditFolder', 'Open');
                    setActiveMaterialContextMenuId(itemIdentifier, itemType);
                    onClose();
                  }}
                  variant='text'
                  contentName='Favorite'
                  contentPiece='ManageFavorite'
                  contentTarget='EditFolder'
                >
                  {t('favorites.changeFolder')}
                </TrackedButton>
              ),
            },
          ),
        };
      }
      case 'renameFolder':
        return {
          header: t('favorites.renameButton'),
          body: (
            <>
              <FormLabel fontSize='sm'>
                {t('favorites.insertNameLabel')}
              </FormLabel>
              <Input
                onChange={(event) => setNameInputValue(event.target.value)}
                value={nameInputValue}
              />
            </>
          ),
          footer: buildFooter(
            t('favorites.saveButton'),
            () => renameFolder(itemIdentifier, nameInputValue),
            {
              primaryButtonCustomProps: {
                isDisabled: nameInputValue.length === 0,
              },
            },
          ),
        };
      case 'createFolder':
        return {
          header: t('favorites.newFolder'),
          body: (
            <>
              <FormLabel fontSize='sm'>
                {t('favorites.insertNameLabel')}
              </FormLabel>
              <TrackedSection
                contentInteraction='ClickButton'
                contentName='Favorite'
                contentPiece='NewFolder'
                contentTarget='InsertName'
              >
                <Input
                  onChange={(event) => setNameInputValue(event.target.value)}
                  value={nameInputValue}
                />
              </TrackedSection>
            </>
          ),
          footer: buildFooter(
            t('favorites.saveButton'),
            () => {
              const newFolderId = addFolder(nameInputValue);
              if (itemIdentifier != null) {
                addFavoriteToFolder(itemIdentifier, newFolderId);
                setActiveMaterialContextMenuId(itemIdentifier, itemType);
              }
            },
            {
              primaryButtonCustomProps: {
                isDisabled: nameInputValue.length === 0,
                contentName: 'Favorite',
                contentPiece: 'NewFolder',
                contentTarget: 'Submit',
              },
              customSecondaryButton: (
                <TrackedButton
                  px='0'
                  fontSize='sm'
                  onClick={() => {
                    setNameInputValue('');
                    onClose();
                  }}
                  variant='text'
                  contentName='Favorite'
                  contentPiece='NewFolder'
                  contentTarget='Cancel'
                >
                  {t('favorites.cancelButton')}
                </TrackedButton>
              ),
            },
          ),
        };
      case 'deleteFolder':
        return {
          header: t('favorites.deleteFolderHeader'),
          body: <Text>{t('favorites.deleteFolderInformation')}</Text>,
          footer: buildFooter(
            t('favorites.deleteButton'),
            () => deleteFolder(itemIdentifier),
            { primaryButtonCustomProps: { variant: 'danger' } },
          ),
        };
    }
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setNameInputValue('');
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent px='6' py='8' bg='background'>
        <ModalHeader>
          <Text>{header}</Text>
          <ModalCloseButton mt='9' mr='9' onClick={trackCloseButton} />
        </ModalHeader>
        <ModalBody fontSize='sm'>{body}</ModalBody>
        <ModalFooter>
          <HStack justifyContent='space-between' w='full'>
            {footer}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
